import * as yup from "yup";
import React from "react";

/**
 * Validate form data against a Yup schema
 * @param {Object} schema - Yup schema to validate against
 * @param {Object} data - Data to validate
 * @returns {Promise<Object>} - { isValid: boolean, errors: Object, data: Object }
 */
export const validateForm = async (schema, data) => {
  try {
    const validatedData = await schema.validate(data, {
      abortEarly: false,
      stripUnknown: true,
    });
    return {
      isValid: true,
      errors: {},
      data: validatedData,
    };
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const errors = {};
      error.inner.forEach((err) => {
        if (err.path) {
          errors[err.path] = err.message;
        }
      });
      return {
        isValid: false,
        errors,
        data: null,
      };
    }
    throw error;
  }
};

/**
 * Validate a single field against a Yup schema
 * @param {Object} schema - Yup schema
 * @param {string} fieldName - Name of the field to validate
 * @param {any} value - Value to validate
 * @returns {Promise<string|null>} - Error message or null if valid
 */
export const validateField = async (schema, fieldName, value) => {
  try {
    await schema.validateAt(fieldName, { [fieldName]: value });
    return null;
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      return error.message;
    }
    throw error;
  }
};

/**
 * Create a form validation hook
 * @param {Object} schema - Yup schema
 * @param {Object} initialValues - Initial form values
 * @returns {Object} - { values, errors, setValue, setError, validate, reset }
 */
export const useFormValidation = (schema, initialValues = {}) => {
  const [values, setValues] = React.useState(initialValues);
  const [errors, setErrors] = React.useState({});
  const [touched, setTouched] = React.useState({});

  const setValue = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const setError = (name, error) => {
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const setTouchedField = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const validate = async () => {
    const result = await validateForm(schema, values);
    setErrors(result.errors);
    return result.isValid;
  };

  const validateField = async (name) => {
    const error = await validateField(schema, name, values[name]);
    setErrors((prev) => ({ ...prev, [name]: error || "" }));
    return !error;
  };

  const reset = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  };

  return {
    values,
    errors,
    touched,
    setValue,
    setError,
    setTouchedField,
    validate,
    validateField,
    reset,
  };
};

/**
 * Sanitize HTML content to prevent XSS
 * @param {string} content - HTML content to sanitize
 * @returns {string} - Sanitized content
 */
export const sanitizeHtml = (content) => {
  if (typeof content !== "string") return content;

  // Basic HTML sanitization - remove script tags and dangerous attributes
  return content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, "")
    .replace(/on\w+="[^"]*"/gi, "")
    .replace(/on\w+='[^']*'/gi, "")
    .replace(/javascript:/gi, "");
};

/**
 * Sanitize form data by applying sanitization to specified fields
 * @param {Object} data - Form data to sanitize
 * @param {Array} fields - Array of field names to sanitize
 * @returns {Object} - Sanitized form data
 */
export const sanitizeFormData = (
  data,
  fields = ["content", "message", "bio", "text"]
) => {
  const sanitized = { ...data };
  fields.forEach((field) => {
    if (sanitized[field]) {
      sanitized[field] = sanitizeHtml(sanitized[field]);
    }
  });
  return sanitized;
};

/**
 * Debounce function for validation
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Create a debounced validation function
 * @param {Object} schema - Yup schema
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} - Debounced validation function
 */
export const createDebouncedValidation = (schema, delay = 300) => {
  return debounce(async (data, callback) => {
    const result = await validateForm(schema, data);
    callback(result);
  }, delay);
};

/**
 * Format validation errors for display
 * @param {Object} errors - Validation errors object
 * @returns {Array} - Array of formatted error messages
 */
export const formatErrors = (errors) => {
  return Object.entries(errors).map(([field, message]) => ({
    field,
    message,
    id: `${field}-error`,
  }));
};

/**
 * Check if form has any errors
 * @param {Object} errors - Validation errors object
 * @returns {boolean} - True if form has errors
 */
export const hasErrors = (errors) => {
  return Object.values(errors).some((error) => error && error.trim() !== "");
};

/**
 * Get first error message
 * @param {Object} errors - Validation errors object
 * @returns {string|null} - First error message or null
 */
export const getFirstError = (errors) => {
  const firstError = Object.values(errors).find(
    (error) => error && error.trim() !== ""
  );
  return firstError || null;
};
